<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { rem2Px, renderMessage, inMiniprograme } from '@/utils/util'
import ThemeAvatarSection from '@/components/business/memorize/theme/ThemeAvatarSection.vue'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK, CODE_LIFE_NOT_OPEN, CODE_LIFE_BANNED, CODE_LIFE_BLOCKED } from '@/apis/code'
import type { CommonRouteTabOption } from '@/components/base/RTab/RRouteTabs.vue'
import { handleErr } from '@/composables/exception/handler'
import { useLifeListStore, type MemorialPrivilegeDTO } from '@/composables/life/life'
import { titleBar } from '@/composables/menu/title'
import type MenuItem from '@/type/MenuItem'
import { type Ref, ref, onMounted, onActivated, onUnmounted, computed, watch, watchEffect, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { THEME_LIGHT } from '@/composables/memorize/memorize'
import {
  selectedTheme,
  setSelectedThemeByServiceId,
  showMemorizeThemeDialog,
  themeServiceId,
  gThemeNow,
  gDefaultThemeColors,
  useThemeSystem
} from '@/composables/theme/theme'
import { displayYearPeriod } from '@/utils/time'
import {
  type MemorialServiceDetailDTO,
  getServiceDetail,
  type MemorialServiceDetailsResponse
} from '@/composables/service/mservice'
import { scrollToTop } from '@/utils/util'
import { useServicesStore } from '@/composables/memorize/store'
import { currentUser, currentUserHandle, isLogin } from '@/composables/user/users'
import ThemePicker from '@/components/business/memorize/theme/ThemePicker.vue'
import { getInvitation as createInvitationLink } from '@/composables/wechat/wechat'
import { auditModeShow } from '@/utils/util'
definePageMeta({
  showBottomNav: false
})

useThemeSystem()
watchEffect(() => currentUser.value)

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const nuxtApp = useNuxtApp() as any
const requestURL = useRequestURL()

const serviceId = ref(route.params.id as string)
type MemorialFetchState = 'loading' | 'ready'

const lifeListStore = useLifeListStore()
const serviceDetailsState = useState<Record<string, MemorialServiceDetailDTO | undefined>>(
  'memorial-service-details-map',
  () => ({})
)
const serviceDetails = computed<MemorialServiceDetailDTO | undefined>({
  get: () => serviceDetailsState.value[serviceId.value],
  set: (value) => {
    serviceDetailsState.value[serviceId.value] = value
  }
})

const serviceDetailsStore = useServicesStore()

const isMember = computed(() => {
  return currentUser.value?.member?.state === 1
})
const canShare = ref(false)

const memorialPrivilegeState = useState<Record<string, MemorialPrivilegeDTO | null>>(
  'memorial-privilege-map',
  () => ({})
)
const memorialPrivilege = computed<MemorialPrivilegeDTO | null>({
  get: () => memorialPrivilegeState.value[serviceId.value] ?? null,
  set: (value) => {
    memorialPrivilegeState.value[serviceId.value] = value
  }
})
const state = ref(0)
const fetchStateState = useState<Record<string, MemorialFetchState>>('memorial-fetch-state-map', () => ({}))
const fetchState = computed<MemorialFetchState>({
  get: () => fetchStateState.value[serviceId.value] ?? 'loading',
  set: (value) => {
    fetchStateState.value[serviceId.value] = value
  }
})
const errorMessage = ref('')
const lifeData = ref()
const hydrated = ref(false)

const avatar = computed(() => {
  return serviceDetails.value?.life.avatar || lifeData.value?.avatar
})

const isCreator = computed(() => serviceDetails.value?.creatorId === currentUserHandle.value)

const canManage = computed(() => {
  const role = serviceDetails.value?.privilege?.role
  return role && (role === 'creator' || role === 'admin')
})

const isTabShow = ref(true)

const showTheme = ref(false)

const bannerHeight = rem2Px(66 / 4)
const MD_BREAKPOINT = 768

const showHeader = ref(false)

const tabs = computed<CommonRouteTabOption[]>(() => [
  {
    to: `/m/${route.params.id}`,
    display: auditModeShow.value ? t('m.memorize_audit') : t('m.memorize')
  },
  {
    to: `/m/${route.params.id}/life`,
    display: t('m.life')
  },
  {
    to: `/m/${route.params.id}/album`,
    display: t('m.album')
  },
  {
    to: `/m/${route.params.id}/story`,
    display: t('m.memory')
  }
])

let wrapperRef: Ref<HTMLElement | null> = ref(null)

const name = computed(() => {
  return serviceDetails.value?.life?.name?.fullName || lifeData.value?.name?.fullName || t('alt.name')
})
const yearPeriod = computed(() => {
  const life = serviceDetails.value?.life || lifeData.value
  if (life) {
    return displayYearPeriod(life.birthday, life.dayDied, life.passAway)
  }
  return ''
})

const pageTitle = computed(() => {
  return `${name.value || ''} ${t('m.memorize')}`.trim()
})

const seoDescription = computed(() => {
  return errorMessage.value || t('m.memorize_desc')
})

const seoImage = computed(() => {
  return (
    serviceDetails.value?.life?.avatar?.url ||
    serviceDetails.value?.life?.avatar?.middleQuality ||
    lifeData.value?.avatar?.url ||
    lifeData.value?.avatar?.middleQuality
  )
})

const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: seoDescription,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

const service = computed(() => {
  return lifeData.value?.memorialServices.filter((item: any) => item.id === serviceId.value)[0]
})

function syncTitleBarBackState() {
  if (!import.meta.client) return
  const isMdUp = window.innerWidth >= MD_BREAKPOINT
  titleBar.value.backable = !isMdUp
  titleBar.value.hideBack = isMdUp
  console.log('！！时序测试！！回到 m 时打开 titlebar')
}
watch(
  () => titleBar.value.backable,
  (newVal: boolean) => {
    console.log('!!debug!!itleBar.value.backable返回按钮显示被修改watch：', newVal)
  }
)

function calcHeaderVisible() {
  titleBar.value.transparent = false
  titleBar.value.reverse = false
}

function calcHeaderInvisible() {
  titleBar.value.transparent = true
  titleBar.value.title = ''
  let isLight = true
  if (gThemeNow().value) {
    isLight = gThemeNow().value?.mode === THEME_LIGHT
  } else {
    isLight = false
  }
  titleBar.value.reverse = !isLight
}

const scrollListener = function () {
  // Get the current scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop

  // 超过tab
  if (scrollTop > bannerHeight) {
    showHeader.value = true

    if (titleBar.value.transparent) {
      calcHeaderVisible()
    }
  } else {
    // 返回tab上
    showHeader.value = false

    if (!titleBar.value.transparent) {
      calcHeaderInvisible()
    }
  }
}

function nav2Life() {
  router.push('/life/' + serviceDetails.value?.lifeId)
}

const reloadTab = async () => {
  isTabShow.value = false
  await nextTick()
  isTabShow.value = true
}

function showThemePicker() {
  showTheme.value = true
}

function showPrivilege() {}

function showVisits() {}

// 处理头像更新
async function handleAvatarUpdate(avatarData: any) {
  if (lifeData.value) lifeData.value.avatar = avatarData
  if (serviceDetails.value?.life) serviceDetails.value.life.avatar = avatarData
}

async function getInvitation() {
  await nextTick()
  if (canShare.value) {
    await createInvitationLink(
      serviceId.value,
      'invitee', //默认值
      30, //默认值
      {
        name: name.value || '', // 纪念的人名
        avatarUrl: titleBar.value.life?.avatar?.middleQuality || titleBar.value.life?.avatar?.url || ''
      },
      null, // 不需要剪贴板
      {
        onSuccess: () => {
          console.log('复制成功')
        },
        onError: () => {
          console.log('复制失败')
        }
      }
    )
  } else {
    console.log('没有权限')
  }
}

const resetCanShare = () => {
  canShare.value =
    memorialPrivilege.value !== undefined &&
    (memorialPrivilege.value?.role === 'creator' || memorialPrivilege.value?.role === 'admin')
}

const initDataWithResponse = (resData: CommonResponse<MemorialServiceDetailsResponse>) => {
  if (resData.code !== CODE_OK) return
  const response = resData.data
  // set theme
  themeServiceId.value = serviceId.value!
  setSelectedThemeByServiceId(themeServiceId.value, response.selectedTheme)
  serviceDetails.value = response.serviceDetails
  titleBar.value.life = response.serviceDetails.life

  titleBar.value.lifeId = response.serviceDetails.life?.id || ''
  lifeData.value = response.serviceDetails.life
  memorialPrivilege.value = serviceDetails.value.privilege
  state.value = 0
  errorMessage.value = ''
  resetCanShare()

  // 微信小程序环境不用自动配置
  if (import.meta.client && !inMiniprograme) {
    getInvitation()
  }
  serviceDetailsStore.addById(response.serviceDetails)

  // 当服务详情加载完成后，设置标题
  const fullName = response.serviceDetails.life?.name?.fullName
  if (fullName) {
    if (import.meta.client)
      document.title = auditModeShow.value
        ? t('m.tag_title_audit', { fullName })
        : inMiniprograme.value
          ? t('m.tag_title_mini', { fullName })
          : t('m.tag_title', { fullName })
  }
}

const applyServiceResponse = (resData: CommonResponse<MemorialServiceDetailsResponse>, showWarning = false) => {
  if (resData.code === CODE_OK) {
    initDataWithResponse(resData)
    fetchState.value = 'ready'
    return
  }
  if (resData.code === CODE_LIFE_NOT_OPEN) {
    state.value = 1
    errorMessage.value = resData.message
    fetchState.value = 'ready'
    return
  }
  if (resData.code === CODE_LIFE_BANNED || resData.code === CODE_LIFE_BLOCKED) {
    state.value = 2
    errorMessage.value = resData.message
    fetchState.value = 'ready'
    return
  }
  fetchState.value = 'ready'
  if (showWarning && import.meta.client) {
    ElMessage({
      message: renderMessage(resData.message),
      type: 'warning'
    })
  }
}

const computedLifeData = () => {
  const life = lifeListStore.searchByServiceId(serviceId.value)
  if (life != undefined) {
    titleBar.value.lifeId = life?.id || ''

    titleBar.value.life = life
    console.log('11111被computedLifeData')
    // 设置标题
    if (life.name.fullName && import.meta.client) {
      document.title = auditModeShow.value
        ? t('m.tag_title_audit', { fullName: life.name.fullName })
        : inMiniprograme.value
          ? t('m.tag_title_mini', { fullName: life.name.fullName })
          : t('m.tag_title', { fullName: life.name.fullName })
    }
    lifeData.value = life
  }
}

const loadMemorialService = async () => {
  if (serviceId.value) {
    fetchState.value = 'loading'
    const { data, error, statusCode } = await getServiceDetail(serviceId.value)
    if (error.value) {
      fetchState.value = 'ready'
      handleErr(data, error, statusCode)
    } else {
      const resData = data as unknown as Ref<CommonResponse<MemorialServiceDetailsResponse>>
      applyServiceResponse(resData.value, true)
    }
  }
}

const init = async () => {
  themeServiceId.value = serviceId.value!

  computedLifeData()
  titleBar.value.transparent = true
  titleBar.value.category = 'lifepage'
  titleBar.value.title = ''
  titleBar.value.hideBack = true
  syncTitleBarBackState()
  titleBar.value.menus = []
  titleBar.value.showMenuCount = 4

  window.addEventListener('scroll', scrollListener)
  window.addEventListener('resize', syncTitleBarBackState)
  await nextTick()

  if (!serviceDetails.value) {
    await loadMemorialService()
  }
}

// Async operations
if (!nuxtApp.payload.data) {
  nuxtApp.payload.data = {}
}
if (nuxtApp.static && !nuxtApp.static.data) {
  nuxtApp.static.data = {}
}
const { data: ssrData } = await useAsyncData(`memorial-details-${serviceId.value}`, async () => {
  const { data } = await getServiceDetail(serviceId.value!)
  return data.value
})
if (ssrData.value) {
  fetchState.value = 'ready'
}

// Watchers
watch(
  () => ssrData.value,
  (value) => {
    if (value) {
      applyServiceResponse(value)
    }
  },
  { immediate: true }
)

watch(
  () => gThemeNow().value,
  (newVal) => {
    if (newVal) {
      if (showHeader.value) {
        calcHeaderVisible()
      } else {
        calcHeaderInvisible()
      }
    }
  }
)

watch(
  () => memorialPrivilege.value,
  (val) => {
    resetCanShare()
    const menus: Array<MenuItem> = []
    titleBar.value.menus = menus
  }
)

// Avatar watcher removed as it is now a computed property
// watch(
//   () => serviceDetails.value?.life.avatar || lifeData.value?.avatar,
//   (newAvatar) => {
//     avatar.value = newAvatar
//   },
//   { immediate: true }
// )

watch(selectedTheme, (val) => {
  if (val) {
    scrollToTop()
  }
})

watch(
  () => serviceDetails.value,
  (newVal) => {
    if (newVal?.life?.name?.fullName) {
      // 当服务详情加载完成后，设置标题
      if (import.meta.client)
        document.title = auditModeShow.value
          ? t('m.tag_title_audit', { fullName: newVal.life.name.fullName })
          : inMiniprograme.value
            ? t('m.tag_title_mini', { fullName: newVal.life.name.fullName })
            : t('m.tag_title', { fullName: newVal.life.name.fullName })
    }
  }
)

// Lifecycle Hooks - Must be at the end to ensure all setup logic is complete
onMounted(async () => {
  hydrated.value = true
  await init()
})

onUnmounted(() => {
  window.removeEventListener('scroll', scrollListener)
  window.removeEventListener('resize', syncTitleBarBackState)
})

onBeforeRouteUpdate(async (to, from) => {
  // only fetch the user if the id changed as maybe only the query or the hash changed
  if (to.params.id !== from.params.id) {
    serviceDetails.value = undefined
    serviceId.value = to.params.id as string
    computedLifeData()
    await loadMemorialService()
  } else if (to.path !== from.path) {
    if (lifeData.value?.name.fullName) {
      document.title = auditModeShow.value
        ? t('m.tag_title_audit', { fullName: lifeData.value.name.fullName })
        : inMiniprograme.value
          ? t('m.tag_title_mini', { fullName: lifeData.value.name.fullName })
          : t('m.tag_title', { fullName: lifeData.value.name.fullName })
    }
  }
})
</script>
<template>
  <div>
    <div v-if="fetchState === 'loading'" min-h-screen w-full bg-theme-white />
    <div
      flex="~ col"
      md:mt--14
      mt--2
      min-h-screen
      relative
      items-center
      bg-theme-white
      :class="{ 'overflow-hidden': showMemorizeThemeDialog }"
      v-else-if="state === 0">
      <ThemeAvatarSection
        :avatar="avatar"
        :name="name"
        :yearPeriod="yearPeriod"
        :isCreator="isCreator"
        :canManage="canManage"
        :serviceId="serviceId"
        @toLife="nav2Life"
        @showTheme="showThemePicker"
        @showPrivilege="showPrivilege"
        @showVisits="showVisits"
        :altar="serviceDetails?.life?.altar as any"
        @updateAvatar="handleAvatarUpdate"
        :passAway="serviceDetails?.life?.passAway" />
      <div ref="wrapperRef" flex="~ col" relative w-full rounded-t items-center>
        <RRouteTabs
          v-if="isTabShow"
          replace
          :theme="true"
          sticky
          :options="tabs"
          w-full
          :themeColors="(gThemeNow()?.value?.themeColors as any) || gDefaultThemeColors"></RRouteTabs>
        <!-- <KeepAlive :max="5"> -->
        <NuxtPage px-2 md:px-0 w-full />
        <!-- </KeepAlive> -->
        <!-- <FloatingMenu v-if="memorialPrivilege" :privilege="memorialPrivilege" :zIndex="1000" :wrapperRef="wrapperRef"
          @onMenuClick="onMenuClicked">
        </FloatingMenu> -->
      </div>
      <ThemePicker v-model="showTheme" :isMember="isMember" />
    </div>
    <div v-else-if="state === 1" v-show="hydrated" min-h-screen w-full flex flex-col justify-center items-center>
      <div class="text-center text-main text-xl">
        <p>{{ errorMessage }}</p>
      </div>
      <div
        btn-outline
        mt-20
        rounded-full
        px-10
        @click="hydrated && isLogin ? router.replace('/home') : router.replace('/login')">
        {{ hydrated && isLogin ? t('invitation.back_to_home') : t('action.to_login') }}
      </div>
    </div>
    <div v-else-if="state === 2" v-show="hydrated" min-h-screen w-full flex flex-col justify-center items-center>
      <div class="i-tabler:ban" style="color: #f44e4e" w-30 h-30></div>
      <div class="text-center text-xl font-bold mt-10">
        <p>{{ errorMessage }}</p>
      </div>
      <div btn-outline mt-10 rounded-full px-10 @click="router.replace('/home')">
        {{ t('invitation.back_to_home') }}
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped></style>
